<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: Template of the pd.PdPartner object view (ObjectBaseComponent). Header with the partner, its lock state and the action bar
  (Sammellauf, Vollabgleich, Probelauf, Export, Jobs einrichten, Sperre lösen); below it one of the four sections
  of the left navigation: Übersicht (metadata form + processing panel), Ereignisse, Sammelläufe, Exportläufe.
-->
@if (initialized && dto) {
    <div class="flex flex-column gap-3 p-3">

        <!-- Header: who the partner is, whether a job holds it, and what can be started -->
        <div class="flex flex-wrap justify-content-between align-items-center gap-3 p-3 surface-card border-1 surface-border border-round-lg">
            <div class="flex align-items-center gap-3">
                <i class="fa-regular fa-right-left text-2xl text-primary"></i>
                <div class="flex flex-column gap-1">
                    <span class="text-xl font-semibold">{{ dto.alias }}</span>
                    <span class="text-sm text-600">
                        Datenaustausch-Partner #{{ dto.id }}
                        @if (dto.partnerDtoName) {
                            &middot; {{ dto.partnerDtoName }}
                        }
                    </span>
                </div>
                <!-- Status chips are spans with PrimeFlex tokens: the shell's global .p-tag override would make every
                     p-tag severity look the same. -->
                <span [class]="processingStatusChipClass">{{ processingStatusLabel }}</span>
                @if (isInactive) {
                    <span [class]="inactiveChipClass">Inaktiv</span>
                }
            </div>

            <div class="flex flex-wrap align-items-center gap-2">
                <!-- Utility actions (left) -->
                <p-button icon="fa-regular fa-arrows-rotate"
                          severity="secondary"
                          [text]="true"
                          [rounded]="true"
                          ariaLabel="Ansicht aktualisieren"
                          pTooltip="Partner, Status und Liste neu laden"
                          tooltipPosition="bottom"
                          [disabled]="runningAction !== null"
                          (onClick)="refreshView()"></p-button>
                <p-button label="Jobs einrichten"
                          icon="fa-regular fa-calendar-plus"
                          severity="secondary"
                          [outlined]="true"
                          pTooltip="Legt die Jobzeilen für Sammellauf und Export als Entwurf an. Geplant laufen sie erst, wenn sie in der Job-Konfiguration aktiviert sind."
                          tooltipPosition="bottom"
                          [loading]="runningAction === 'provision'"
                          [disabled]="runningAction !== null"
                          (onClick)="provisionJobs()"></p-button>
                @if (isLocked) {
                    <p-button label="Sperre lösen"
                              icon="fa-regular fa-lock-open"
                              severity="danger"
                              [outlined]="true"
                              pTooltip="Gibt eine hängende Sperre frei. Der Server lehnt ab, solange der sperrende Joblauf noch aktiv ist."
                              tooltipPosition="bottom"
                              [loading]="runningAction === 'release'"
                              [disabled]="runningAction !== null"
                              (onClick)="confirmReleaseLock()"></p-button>
                }

                <!-- Main actions (right); the primary one is last -->
                <p-button label="Probelauf"
                          icon="fa-regular fa-flask"
                          severity="secondary"
                          [outlined]="true"
                          pTooltip="Sammellauf, der nur zählt und nichts speichert – der Delta-Zeitpunkt bleibt unverändert."
                          tooltipPosition="bottom"
                          [loading]="runningAction === 'dryRun'"
                          [disabled]="startDisabled"
                          (onClick)="startDryRun()"></p-button>
                <p-button label="Vollabgleich starten"
                          icon="fa-regular fa-list-check"
                          severity="secondary"
                          [outlined]="true"
                          pTooltip="Prüft alle Verträge des Partners gegen den Stand am Stichtag, auch ohne Änderungsdatum."
                          tooltipPosition="bottom"
                          [loading]="runningAction === 'full'"
                          [disabled]="startDisabled"
                          (onClick)="startFullCollection()"></p-button>
                <p-button label="Export starten"
                          icon="fa-regular fa-paper-plane"
                          severity="secondary"
                          pTooltip="Gibt die offenen Ereignisse über den Exportkanal des Partners aus."
                          tooltipPosition="bottom"
                          [loading]="runningAction === 'export'"
                          [disabled]="startDisabled"
                          (onClick)="startExport()"></p-button>
                <p-button label="Sammellauf starten"
                          icon="fa-regular fa-play"
                          severity="primary"
                          pTooltip="Ermittelt die Vertragsereignisse seit dem letzten Delta-Zeitpunkt."
                          tooltipPosition="bottom"
                          [loading]="runningAction === 'collection'"
                          [disabled]="startDisabled"
                          (onClick)="startCollection()"></p-button>
            </div>
        </div>

        @if (isInactive) {
            <p-message severity="info" styleClass="w-full">{{ inactiveHint }}</p-message>
        }

        @if (isLocked) {
            <p-message severity="warn" styleClass="w-full">{{ lockHint }}</p-message>
        }

        @switch (activeNavigationItems?.action) {

            @case ('events') {
                <!-- ==================== Ereignisse ==================== -->
                @if (eventsWidget) {
                    <!-- AI: Widget for the pd.PdPartnerContractEvent ledger of this partner -->
                    <mvs-widget #eventsWidgetRef
                                [widgetData]="eventsWidget"
                                (onObjectSelect)="handleEventSelect($event)">

                        <!-- Ereignisdaten: the payload in one line, worded by event type -->
                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" [fieldId]="customColumns.eventPayload.fieldId"
                                     [label]="customColumns.eventPayload.label">
                            <span class="text-sm">{{ eventPayloadText(object) || '–' }}</span>
                        </ng-template>

                        <!-- Ersetzt Ereignis: opens the superseded event in the generic object view -->
                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" [fieldId]="customColumns.eventSupersedes.fieldId"
                                     [label]="customColumns.eventSupersedes.label">
                            @if (supersededEventId(object); as supersededId) {
                                <p-button [label]="'Ereignis #' + supersededId"
                                          icon="fa-regular fa-arrow-up-right-from-square"
                                          iconPos="right"
                                          size="small"
                                          [link]="true"
                                          pTooltip="Ersetztes Ereignis öffnen"
                                          tooltipPosition="top"
                                          (onClick)="openSupersededEvent(object, $event)"></p-button>
                            } @else {
                                <span class="text-sm text-500">–</span>
                            }
                        </ng-template>
                    </mvs-widget>
                }
            }

            @case ('collectionRuns') {
                <!-- ==================== Sammelläufe ==================== -->
                @if (collectionRunsWidget) {
                    <!-- AI: Widget for the pd.PdPartnerCollectionRun list of this partner -->
                    <mvs-widget #collectionRunsWidgetRef
                                [widgetData]="collectionRunsWidget"
                                (onObjectSelect)="handleCollectionRunSelect($event)">
                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" [fieldId]="customColumns.runMessage.fieldId"
                                     [label]="customColumns.runMessage.label">
                            <span class="inline-block max-w-20rem white-space-nowrap overflow-hidden text-overflow-ellipsis vertical-align-bottom text-sm"
                                  [pTooltip]="runMessage(object)"
                                  tooltipPosition="top">{{ runMessage(object) || '–' }}</span>
                        </ng-template>
                    </mvs-widget>
                }
            }

            @case ('exportRuns') {
                <!-- ==================== Exportläufe ==================== -->
                @if (exportRunsWidget) {
                    <!-- AI: Widget for the pd.PdPartnerExportRun list of this partner -->
                    <mvs-widget #exportRunsWidgetRef
                                [widgetData]="exportRunsWidget"
                                (onObjectSelect)="handleExportRunSelect($event)">
                        <ng-template let-object="object" mvs-widget-custom-field-directive
                                     area="columns" [fieldId]="customColumns.runMessage.fieldId"
                                     [label]="customColumns.runMessage.label">
                            <span class="inline-block max-w-20rem white-space-nowrap overflow-hidden text-overflow-ellipsis vertical-align-bottom text-sm"
                                  [pTooltip]="runMessage(object)"
                                  tooltipPosition="top">{{ runMessage(object) || '–' }}</span>
                        </ng-template>
                    </mvs-widget>
                }
            }

            @default {
                <!-- ==================== Übersicht (default) ==================== -->
                <div class="grid">
                    <div class="col-12 xl:col-8">
                        <p-message severity="info" styleClass="w-full mb-3">
                            „Gesellschaft“ und „Änderungen ab“ sind nach dem ersten Sammellauf ohne Probelauf
                            schreibgeschützt, der Alias, sobald für den Partner Jobs eingerichtet sind. Während einer
                            laufenden Verarbeitung lehnt der Server Änderungen an Gesellschaft und „Änderungen ab“ ab.
                        </p-message>

                        @if (objectIdentifier?.objectId) {
                            <!-- AI: CRUD Object Component for the pd.PdPartner master data (metadata form) -->
                            <mvs-crud-object-generic
                                    [objectType]="'pd.PdPartner'"
                                    [objectId]="objectIdentifier.objectId"
                                    (onFormDirty)="handlePartnerFormDirty($event)"
                                    (onChangedObject)="handlePartnerFormChanged($event)">
                            </mvs-crud-object-generic>
                        }
                    </div>
                    <div class="col-12 xl:col-4">
                        <mvs-pd-partner-processing-status [partner]="dto"></mvs-pd-partner-processing-status>
                    </div>
                </div>
            }
        }
    </div>
} @else if (busy) {
    <!-- Loading: skeletons of header and content -->
    <div class="flex flex-column gap-3 p-3">
        <p-skeleton height="5rem" borderRadius="0.5rem"></p-skeleton>
        <p-skeleton height="20rem" borderRadius="0.5rem"></p-skeleton>
    </div>
} @else {
    <div class="p-3">
        <p-message severity="error" styleClass="w-full">
            Der Datenaustausch-Partner konnte nicht geladen werden. Er ist gelöscht oder Ihnen fehlt das Leserecht.
            Öffnen Sie ihn erneut über die Konfiguration „Partnerdatenaustausch“.
        </p-message>
    </div>
}
